/**
 * useTheme — manages colour palette selection via data-theme on <html>.
 * Persisted to localStorage (immediate) and to users.sys_theme via API (cross-device).
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export type ThemeId =
  | 'light-green'
  | 'light-blue'
  | 'light-orange'
  | 'light-pink'
  | 'light-red'
  | 'light-purple'
  | 'light-teal'
  | 'light-amber'
  | 'light-sepia'
  | 'light-high-contrast'
  | 'dark-black'
  | 'dark-midnight'
  | 'dark-green'
  | 'dark-orange'
  | 'dark-pink'
  | 'dark-red'
  | 'dark-purple'
  | 'dark-teal'
  | 'dark-nord'
  | 'dark-amoled'
  | 'dark-solarized'
  | 'dark-dracula'
  | 'dark-monokai'
  | 'dark-gruvbox'
  | 'dark-catppuccin'
  | 'dark-tokyo-night'
  | 'dark-cyberpunk'
  | 'dark-slate'
  | 'dark-synthwave'
  | 'dark-matrix'
  | 'light-cotton-candy'
  | 'light-mint'
  | 'light-lavender'
  | 'light-ocean'
  | 'light-peach'
  | 'light-forest'
  | 'light-newspaper'
  | 'light-brg'
  | 'dark-ember'
  | 'dark-sunset'
  | 'dark-aurora'
  | 'high-contrast';

export interface ThemeOption {
  id: ThemeId;
  label: string;
  mode: 'light' | 'dark';
  swatch: string; // preview colour dot
  swatchAlt?: string; // secondary swatch
}

export const THEMES: ThemeOption[] = [
  {
    id: 'light-green',
    label: 'Default (Green)',
    mode: 'light',
    swatch: '#2d9b5e',
    swatchAlt: '#56b67f',
  },
  { id: 'light-blue', label: 'Blue', mode: 'light', swatch: '#2563eb', swatchAlt: '#60a5fa' },
  { id: 'light-orange', label: 'Orange', mode: 'light', swatch: '#ea580c', swatchAlt: '#fb923c' },
  { id: 'light-pink', label: 'Pink', mode: 'light', swatch: '#db2777', swatchAlt: '#f472b6' },
  { id: 'light-red', label: 'Red', mode: 'light', swatch: '#dc2626', swatchAlt: '#f87171' },
  { id: 'light-purple', label: 'Purple', mode: 'light', swatch: '#9333ea', swatchAlt: '#c084fc' },
  { id: 'light-teal', label: 'Teal', mode: 'light', swatch: '#0d9488', swatchAlt: '#2dd4bf' },
  { id: 'light-amber', label: 'Amber', mode: 'light', swatch: '#d97706', swatchAlt: '#fbbf24' },
  { id: 'light-sepia', label: 'Sepia', mode: 'light', swatch: '#faf4e8', swatchAlt: '#8b6f3e' },
  {
    id: 'light-cotton-candy',
    label: 'Cotton Candy',
    mode: 'light',
    swatch: '#fce8f4',
    swatchAlt: '#c040a8',
  },
  { id: 'light-mint', label: 'Mint', mode: 'light', swatch: '#e6faf5', swatchAlt: '#0d9478' },
  {
    id: 'light-lavender',
    label: 'Lavender',
    mode: 'light',
    swatch: '#f3e8ff',
    swatchAlt: '#7c4fc0',
  },
  { id: 'light-ocean', label: 'Ocean', mode: 'light', swatch: '#eef6fa', swatchAlt: '#0878a8' },
  { id: 'light-peach', label: 'Peach', mode: 'light', swatch: '#fdf5f0', swatchAlt: '#c85830' },
  { id: 'light-forest', label: 'Forest', mode: 'light', swatch: '#f2f5ef', swatchAlt: '#3d7a28' },
  {
    id: 'light-newspaper',
    label: 'Newspaper',
    mode: 'light',
    swatch: '#f5f0e8',
    swatchAlt: '#484040',
  },
  {
    id: 'light-brg',
    label: 'British Racing Green',
    mode: 'light',
    swatch: '#006b3c',
    swatchAlt: '#2e8c5e',
  },
  {
    id: 'light-high-contrast',
    label: 'High Contrast',
    mode: 'light',
    swatch: '#ffffff',
    swatchAlt: '#000000',
  },
  { id: 'dark-black', label: 'Black', mode: 'dark', swatch: '#0a0a0a', swatchAlt: '#404040' },
  { id: 'dark-midnight', label: 'Midnight', mode: 'dark', swatch: '#0b1120', swatchAlt: '#2563eb' },
  { id: 'dark-green', label: 'Dark Green', mode: 'dark', swatch: '#0f1f13', swatchAlt: '#22c55e' },
  {
    id: 'dark-orange',
    label: 'Dark Orange',
    mode: 'dark',
    swatch: '#1a0f00',
    swatchAlt: '#ea580c',
  },
  { id: 'dark-pink', label: 'Hot Pink', mode: 'dark', swatch: '#1c1218', swatchAlt: '#ec4899' },
  { id: 'dark-red', label: 'Dark Red', mode: 'dark', swatch: '#1c1212', swatchAlt: '#ef4444' },
  {
    id: 'dark-purple',
    label: 'Dark Purple',
    mode: 'dark',
    swatch: '#181420',
    swatchAlt: '#a855f7',
  },
  { id: 'dark-teal', label: 'Dark Teal', mode: 'dark', swatch: '#0d1f1d', swatchAlt: '#14b8a6' },
  { id: 'dark-nord', label: 'Nord', mode: 'dark', swatch: '#2e3440', swatchAlt: '#88c0d0' },
  { id: 'dark-amoled', label: 'AMOLED', mode: 'dark', swatch: '#000000', swatchAlt: '#4da6ff' },
  {
    id: 'dark-solarized',
    label: 'Solarized',
    mode: 'dark',
    swatch: '#002b36',
    swatchAlt: '#2aa198',
  },
  { id: 'dark-dracula', label: 'Dracula', mode: 'dark', swatch: '#282a36', swatchAlt: '#bd93f9' },
  { id: 'dark-monokai', label: 'Monokai', mode: 'dark', swatch: '#272822', swatchAlt: '#a6e22e' },
  { id: 'dark-gruvbox', label: 'Gruvbox', mode: 'dark', swatch: '#282828', swatchAlt: '#fe8019' },
  {
    id: 'dark-catppuccin',
    label: 'Catppuccin',
    mode: 'dark',
    swatch: '#1e1e2e',
    swatchAlt: '#cba6f7',
  },
  {
    id: 'dark-tokyo-night',
    label: 'Tokyo Night',
    mode: 'dark',
    swatch: '#1a1b26',
    swatchAlt: '#7aa2f7',
  },
  {
    id: 'dark-cyberpunk',
    label: 'Cyberpunk',
    mode: 'dark',
    swatch: '#0d0d1a',
    swatchAlt: '#00fff5',
  },
  { id: 'dark-slate', label: 'Slate', mode: 'dark', swatch: '#0f172a', swatchAlt: '#94a3b8' },
  {
    id: 'dark-synthwave',
    label: 'Synthwave',
    mode: 'dark',
    swatch: '#1a0c30',
    swatchAlt: '#ff6ec7',
  },
  { id: 'dark-matrix', label: 'Matrix', mode: 'dark', swatch: '#001200', swatchAlt: '#00cc00' },
  { id: 'dark-ember', label: 'Ember', mode: 'dark', swatch: '#1a100a', swatchAlt: '#ff6b1a' },
  { id: 'dark-sunset', label: 'Sunset', mode: 'dark', swatch: '#1a0e0e', swatchAlt: '#ff7860' },
  { id: 'dark-aurora', label: 'Aurora', mode: 'dark', swatch: '#0a1420', swatchAlt: '#30f0c8' },
  {
    id: 'high-contrast',
    label: 'High Contrast',
    mode: 'dark',
    swatch: '#000000',
    swatchAlt: '#ffffff',
  },
];

const STORAGE_KEY = 'jlp-theme';
const DEFAULT_THEME: ThemeId = 'light-green';

/**
 * Module-level flag: set to true while firm theme enforcement is active.
 * Prevents personal-theme changes from overriding the firm's forced theme.
 */
let _firmEnforced = false;

/** Called by FirmBrandEffect (App.tsx) to activate/deactivate firm enforcement. */
export function setFirmEnforced(value: boolean): void {
  _firmEnforced = value;
}

function applyTheme(id: ThemeId) {
  // While firm enforcement is active, personal theme changes are suppressed.
  if (_firmEnforced) return;
  const root = document.documentElement;
  const isDark = THEMES.find((t) => t.id === id)?.mode === 'dark';
  if (id === 'light-green') {
    root.removeAttribute('data-theme'); // use :root defaults
  } else {
    root.setAttribute('data-theme', id);
  }
  // Tell the browser to render native controls (date pickers, scrollbars, etc.)
  // in the correct light/dark mode so they match the active theme.
  root.style.colorScheme = isDark ? 'dark' : 'light';
}

export function useTheme(isAuthenticated?: boolean) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && THEMES.some((t) => t.id === stored)) return stored as ThemeId;
    } catch {
      /* ignore */
    }
    return DEFAULT_THEME;
  });

  // Apply on mount and whenever theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Sync theme from DB once auth is confirmed — runs whenever isAuthenticated transitions to true.
  // Guard prevents firing before the token is set (avoids 401s during code exchange on first load).
  useEffect(() => {
    if (!isAuthenticated) return;
    api
      .get('/api/users/details')
      .then((res) => {
        const dbTheme = res.data?.sys_theme as string | undefined;
        if (dbTheme && THEMES.some((t) => t.id === dbTheme) && dbTheme !== theme) {
          setThemeState(dbTheme as ThemeId);
          try {
            localStorage.setItem(STORAGE_KEY, dbTheme);
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => {
        /* network error — stay with localStorage value */
      });
  }, [isAuthenticated]); // re-runs when auth state becomes true

  const setTheme = useCallback((id: ThemeId) => {
    setThemeState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
    // Persist to DB — fire and forget
    api.put('/api/users/theme', { sys_theme: id }).catch((err) => {
      console.error(
        '[useTheme] Failed to save theme preference:',
        err?.response?.data ?? err.message,
      );
    });
  }, []);

  const isDark = THEMES.find((t) => t.id === theme)?.mode === 'dark';

  return { theme, setTheme, isDark, themes: THEMES };
}

/**
 * Apply (or remove) firm brand colour overrides on :root.
 *
 * When enforce is true:
 *   - The default theme is forced (data-theme removed) so the firm's light
 *     identity is used instead of each user's personal dark/custom theme.
 *   - --primary-500 and --primary-400 are set as inline styles so the firm's
 *     primary colour takes precedence over the default theme's own values.
 * When enforce is false (or primary is absent), those overrides are removed
 * so the active theme's own tokens take effect again.
 *
 * Called from BrandColors.tsx after a successful save and from
 * FirmBrandEffect (App.tsx) on every authenticated page load.
 */
export function applyFirmBrandOverrides(
  primary: string | null | undefined,
  accent: string | null | undefined,
  enforce: boolean,
) {
  const root = document.documentElement;
  if (enforce && primary) {
    // Force the default (light) theme so personal dark/custom themes are overridden.
    root.removeAttribute('data-theme');
    root.style.colorScheme = 'light';
    root.style.setProperty('--primary-500', primary);
    if (accent) root.style.setProperty('--primary-400', accent);
  } else {
    root.style.removeProperty('--primary-500');
    root.style.removeProperty('--primary-400');
    // Restore colour-scheme to light (default); user's own applyTheme will
    // override this immediately if they have a dark theme selected.
    root.style.colorScheme = 'light';
  }
}
